import { createReducer } from '@reduxjs/toolkit';

import difference from 'lodash/difference';
import { collectDatasets } from 'Internals/pipes';
import { disconnected } from '../thunks/subscriptions.thunks';
import {
  pipeAdded,
  pipeUpdated,
  pipeLoaded,
  pipesLoadAllFinished,
  pipesRemoved,
} from '../thunks/pipes.thunks';

import type { LookupMap } from 'Types/common.types';
import type { Pipe, PipeResponse } from 'Types/pipes.types';

type LookupsState = LookupMap;

export const initialState: LookupsState = {};

function _setLookup(datasetIds: string[], pipe: Pipe | PipeResponse, state: LookupsState) {
  for (const id of datasetIds) {
    if (state[id]) {
      state[id][pipe._id] = true;
    } else {
      state[id] = { [pipe._id]: true };
    }
  }
}

function _clearLookup(datasetIds: string[], pipe: Pipe | PipeResponse, state: LookupsState) {
  for (const id of datasetIds) {
    if (state[id] && state[id][pipe._id]) {
      // We are in the immer produce function so it's okay
      // but beware if used outside of this context, mutates state
      delete state[id][pipe._id];
    }
  }
}

function setLookup(pipe: Pipe | PipeResponse, state: LookupsState) {
  // Beware, modifies in place
  const lookupDatasetIds = collectDatasets(pipe); //todo typing
  if (lookupDatasetIds.length < 1) {
    return;
  } else {
    _setLookup(lookupDatasetIds, pipe, state);
  }
}

/*
Note: We are using the builder callback for creating a typesafe reducer.
See https://redux-toolkit.js.org/usage/usage-with-typescript#building-type-safe-reducer-argument-objects
*/
export const lookupsReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(disconnected, () => initialState)
    .addCase(pipeAdded, (state, action) => {
      setLookup(action.payload, state);
    })
    .addCase(pipeUpdated, (state, action) => {
      const newLookupDatasetIds = collectDatasets(action.payload.pipe); //todo typing
      const oldLookupDatasetIds = collectDatasets(action.payload.oldPipe);

      const toAdd = difference(newLookupDatasetIds, oldLookupDatasetIds);
      const toDelete = difference(oldLookupDatasetIds, newLookupDatasetIds);
      if (toAdd.length > 0) {
        _setLookup(toAdd, action.payload.pipe, state);
      }
      if (toDelete.length > 0) {
        _clearLookup(toDelete, action.payload.oldPipe, state);
      }
    })
    .addCase(pipeLoaded, (state, action) => {
      setLookup(action.payload.pipe, state);
    })
    .addCase(pipesLoadAllFinished, (state, action) => {
      const newState = {};
      for (let i = 0; i < action.payload.length; i++) {
        setLookup(action.payload[i], newState);
      }
      return newState;
    })
    .addCase(pipesRemoved, (state, action) => {
      action.payload.forEach((p) => {
        for (const id of p.sourceDatasetIds) {
          if (state[id]) {
            delete state[id][p.id];
          }
        }
      });
    })
);
